import { useEffect, useState, type ReactNode } from "react";
import { NavLink } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { portfolioBadge, useNavItems, useNavBadges } from "./Sidebar.js";
import { NAV_POSITIONS_ATTR } from "../lib/flyToNav.js";
import { AppIcon } from "./AppIcon.js";
import { VenueLogo } from "./VenueLogo.js";
import { VENUE_STATE_META } from "./TopBar.js";
import { showCapabilities } from "./CapabilitiesModal.js";
import { showTour } from "./WelcomeTour.js";
import { useAuthMe } from "../lib/queries.js";
import { useRelayStatus } from "../lib/ws.js";
import { useOddsFormat, ODDS_FORMATS } from "../lib/odds.js";
import { useOrderSettings, setOrderSetting } from "../lib/settings.js";
import { useTradingMode, setTradingMode, useVenueStatus, fmtVenueBalance, type VenueState } from "../lib/venues.js";
import { fmtMoney } from "../lib/format.js";
import { disconnect as omDisconnect, SCOPES, useCanTrade, useIsAuthed } from "../lib/omauth.js";
import { SignInButton } from "./SignInWithOpenMarkets.js";
import { showUnlock } from "./Gate.js";
import { useCanInstall, isStandalone, isIOS, promptInstall } from "../lib/pwa.js";

/**
 * Mobile navigation drawer (<lg). The desktop left rail is hidden on small screens; the top
 * bar's hamburger opens this slide-in drawer with the same routes + live badges — plus, below
 * the routes, everything the desktop top-bar cluster shows that doesn't fit on a phone bar:
 * trading mode, venues & balances, odds format, order settings, API usage, connection status,
 * and account actions. Each section gets real breathing room; nothing here is a toolbar.
 * Desktop is unaffected — this only renders on small screens.
 */
export function MobileNav({ open, onClose }: { open: boolean; onClose: () => void }) {
  const badges = useNavBadges();
  const { live } = badges;
  const navItems = useNavItems();
  const authed = useIsAuthed();

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <div className={`fixed inset-0 z-50 lg:hidden ${open ? "" : "pointer-events-none"}`} aria-hidden={!open}>
      {/* backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/50 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
      />
      {/* drawer */}
      <nav
        className={`absolute left-0 top-0 flex h-full w-[19rem] max-w-[85vw] flex-col border-r border-border bg-panel-2 shadow-2xl transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border pl-4 pr-1.5">
          <AppIcon size={24} />
          <span className="text-sm font-semibold tracking-tight text-ink">greenbook</span>
          <button
            onClick={onClose}
            aria-label="close menu"
            className="ml-auto grid h-11 w-11 place-items-center rounded-md text-dim hover:bg-raised hover:text-ink"
          >
            ✕
          </button>
        </div>

        {/* one scroll region: routes first, then the account/settings sections */}
        <div className="flex-1 overflow-y-auto pb-8">
          <div className="space-y-0.5 p-2">
            {navItems.map((it) => {
              const pb = it.to === "/portfolio" ? portfolioBadge(badges) : null;
              const count = pb?.count ?? 0;
              const urgent = !!pb?.urgent;
              const pill = urgent ? "bg-down/15 text-down" : live ? "bg-up/15 text-up" : "bg-amber/15 text-amber";
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={onClose}
                  // Landing pad for the order parcel while the drawer is open (lib/flyToNav.ts).
                  {...(it.to === "/portfolio" ? { [NAV_POSITIONS_ATTR]: "" } : {})}
                  className={({ isActive }) =>
                    `flex min-h-11 items-center gap-3 rounded-md px-3 py-2.5 text-[15px] ${
                      isActive ? "bg-raised text-ink" : "text-dim hover:bg-raised/60 hover:text-ink"
                    }`
                  }
                >
                  <span className="grid h-5 w-5 shrink-0 place-items-center">{it.icon}</span>
                  <span className="flex-1">{it.label}</span>
                  {count > 0 && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[11px] font-semibold tabular-nums ${pill}`} title={pb?.label} aria-label={pb?.label}>
                      {count}
                    </span>
                  )}
                </NavLink>
              );
            })}
          </div>

          {/* Signed out: no trading/venue/account sections (they'd need a session) — just
              preferences that work locally, and a Sign in card. */}
          {authed ? <TradingSection /> : <SignedOutSection />}
          <PreferencesSection />
          <InstallSection onClose={onClose} />
          {authed && <AccountSection onClose={onClose} />}
        </div>
      </nav>
    </div>
  );
}

/** Signed-out drawer block: what signing in unlocks + the button. */
function SignedOutSection() {
  return (
    <Section label="Account">
      <p className="text-[13px] leading-relaxed text-dim">
        You’re browsing public market data. Sign in with your OpenMarkets account (free works) to
        trade, see your positions and unlock the rest.
      </p>
      <SignInButton label="Sign in with OpenMarkets" size="lg" feedback />
    </Section>
  );
}

/** Labeled drawer section with generous spacing — the anti-toolbar. */
function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-t border-border px-4 pb-5 pt-4">
      <div className="pb-3 text-[11px] font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/** Full Paper/Real switch (with the trade-scope lock story) + the venues/balances summary. */
function TradingSection() {
  const mode = useTradingMode();
  const canTrade = useCanTrade();
  return (
    <Section label="Trading">
      <div>
        <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-border-2 text-sm font-semibold">
          <button
            onClick={() => setTradingMode("paper")}
            className={`min-h-11 ${mode === "paper" ? "bg-blue/20 text-blue" : "text-dim active:bg-raised"}`}
          >
            Paper
          </button>
          <button
            onClick={() => (canTrade ? setTradingMode("real") : showUnlock(SCOPES.trade))}
                className={`min-h-11 border-l border-border-2 ${
              mode === "real" ? "bg-down/25 text-down" : canTrade ? "text-dim active:bg-raised" : "text-faint/60 active:bg-raised"
            }`}
          >
            Real{!canTrade && " 🔒"}
          </button>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-faint">
          {!canTrade
            ? "Real-money trading isn’t on your OpenMarkets plan yet — unlock it to trade with real money. Paper works now."
            : mode === "paper"
              ? "Paper books the internal book on any venue with play money."
              : "Real routes orders to your connected, funded venues."}
        </p>
        {!canTrade && (
          <button
            onClick={() => showUnlock(SCOPES.trade)}
                className="mt-2 min-h-11 w-full rounded-lg bg-up/20 text-sm font-semibold text-up active:bg-up/30"
          >
            Unlock real-money trading
          </button>
        )}
      </div>
      <VenuesBlock />
    </Section>
  );
}

/**
 * Venues summary: one calm row (paper wallet in Paper mode; live count + real balance in
 * Real), expanding to the full per-venue breakdown + connect CTA. Same data as the desktop
 * VenuesChip popover, laid out for a phone.
 */
function VenuesBlock() {
  const vs = useVenueStatus();
  const mode = useTradingMode();
  const paper = mode === "paper";
  const [expanded, setExpanded] = useState(false);

  const rank: Record<VenueState, number> = { live: 0, nofunds: 1, off: 2 };
  const realSorted = [...vs.realVenues].sort(
    (a, b) => rank[a.state] - rank[b.state] || (b.balance ?? 0) - (a.balance ?? 0),
  );

  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-11 w-full items-center gap-2 px-3 text-left active:bg-raised/60"
      >
        <span className="text-[13px] font-medium text-ink">Venues</span>
        <span className="ml-auto flex items-center gap-1.5 text-[13px] text-dim">
          {paper ? (
            <span className="nums text-blue">{fmtVenueBalance(vs.paperWallet?.balance ?? 0, "ATLAS")} paper</span>
          ) : (
            <>
              <span className={`h-1.5 w-1.5 rounded-full ${vs.liveCount > 0 ? "bg-up" : "bg-faint"}`} />
              <span>
                {vs.liveCount} live · <span className="nums">{fmtMoney(vs.realBalance)}</span>
              </span>
            </>
          )}
        </span>
        <span className={`text-faint transition-transform ${expanded ? "rotate-180" : ""}`}>▾</span>
      </button>

      {expanded && (
        <div className="border-t border-border">
          {vs.paperWallet && (
            <div className={`flex min-h-11 items-center justify-between gap-3 px-3 py-2 ${paper ? "bg-blue/5" : ""}`}>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-ink">Paper wallet</div>
                <div className="text-[11px] text-faint">Play money · the internal book</div>
              </div>
              <span className="nums shrink-0 text-[13px] text-blue">
                {fmtVenueBalance(vs.paperWallet.balance, "ATLAS")}
              </span>
            </div>
          )}
          {vs.loading ? (
            <div className="border-t border-border/60 px-3 py-3 text-[13px] text-faint">loading venues…</div>
          ) : realSorted.length === 0 ? (
            <div className="border-t border-border/60 px-3 py-3 text-[13px] text-faint">
              No real venues connected yet.
            </div>
          ) : (
            realSorted.map((v) => {
              const m = VENUE_STATE_META[v.state];
              return (
                <div key={v.partnerId} className="flex min-h-11 items-center gap-2.5 border-t border-border/60 px-3 py-1.5">
                  <VenueLogo name={v.name} logoUrl={v.logoUrl} size={20} status={v.state} />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words text-[13px] text-ink">{v.name}</span>
                    <span className={`text-[11px] ${m.text}`}>{m.label}</span>
                  </span>
                  <span className="nums shrink-0 text-[12px] text-dim">{fmtVenueBalance(v.balance, v.currency)}</span>
                </div>
              );
            })
          )}
          <a
            href="https://app.openmarkets.ai"
            target="_blank"
            rel="noreferrer"
            className="flex min-h-11 items-center justify-center border-t border-border px-3 text-[13px] font-semibold text-amber active:bg-amber/5"
          >
            Connect or fund venues ↗
          </a>
        </div>
      )}
    </div>
  );
}

/** Odds format + order settings, with labels and room — not icon soup. */
function PreferencesSection() {
  const authed = useIsAuthed();
  const { format, setFormat } = useOddsFormat();
  const settings = useOrderSettings();
  const qc = useQueryClient();
  return (
    <Section label="Preferences">
      <div>
        <div className="pb-2 text-[13px] font-medium text-ink">Odds format</div>
        <div className="grid grid-cols-3 overflow-hidden rounded-lg border border-border-2 text-[13px]">
          {ODDS_FORMATS.map((f, i) => (
            <button
              key={f.value}
              onClick={() => setFormat(f.value)}
              className={`min-h-11 ${i > 0 ? "border-l border-border-2" : ""} ${
                format === f.value ? "bg-amber/20 font-semibold text-amber" : "text-dim active:bg-raised"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {authed && (
      <label className="flex min-h-11 cursor-pointer items-start gap-3">
        <input
          type="checkbox"
          checked={settings.enable_limit_orders}
          onChange={(e) => {
            setOrderSetting("enable_limit_orders", e.target.checked);
            qc.invalidateQueries({ queryKey: ["config"] });
          }}
          className="mt-0.5 h-5 w-5 shrink-0 accent-current"
        />
        <span className="min-w-0">
          <span className="block text-[13px] font-medium text-ink">Enable limit orders</span>
          <span className="mt-0.5 block text-[11px] leading-snug text-faint">
            Resting limit orders (the venue must also support them). Off → market/IOC only. Stored in this browser.
          </span>
        </span>
      </label>
      )}
    </Section>
  );
}

/** A full-width, thumb-sized drawer action row. */
function ActionRow({ danger, onClick, children }: { danger?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`-mx-2 flex min-h-11 w-[calc(100%+1rem)] items-center rounded-md px-2 text-left text-[13px] font-medium ${
        danger ? "text-down active:bg-down/10" : "text-ink active:bg-raised"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Install-to-home-screen affordance. Hidden once the app is already installed. On
 * Chrome/Android it's a button that fires the native install prompt; on iOS (no prompt
 * API) it's a short "Share → Add to Home Screen" hint. On desktop it simply doesn't show.
 */
function InstallSection({ onClose }: { onClose: () => void }) {
  const canInstall = useCanInstall();
  const [standalone] = useState(isStandalone);
  if (standalone) return null;

  if (canInstall) {
    return (
      <Section label="App">
        <ActionRow
          onClick={async () => {
            onClose();
            await promptInstall();
          }}
        >
          <span className="mr-2" aria-hidden>
            📲
          </span>{" "}
          Install greenbook
        </ActionRow>
        <p className="text-[12px] leading-relaxed text-dim">
          Adds a home-screen icon and opens full-screen — no browser address bar or toolbar.
        </p>
      </Section>
    );
  }

  if (isIOS()) {
    return (
      <Section label="App">
        <p className="text-[13px] leading-relaxed text-ink">
          Install greenbook for a full-screen app: tap{" "}
          <span className="font-semibold text-amber">Share</span> then{" "}
          <span className="font-semibold text-amber">“Add to Home Screen.”</span>
        </p>
        <p className="mt-1 text-[12px] leading-relaxed text-dim">
          No browser address bar or toolbar once you open it from the icon.
        </p>
      </Section>
    );
  }

  return null;
}

/** Who you are (key, scopes, credits), API usage, live-data status, and the account actions. */
function AccountSection({ onClose }: { onClose: () => void }) {
  const { data, isLoading, isError } = useAuthMe();
  const relay = useRelayStatus();
  const ws = {
    connected: { dot: "bg-up", text: "text-up", label: "Live data connected" },
    reconnecting: { dot: "bg-amber", text: "text-amber", label: "Reconnecting…" },
    down: { dot: "bg-faint", text: "text-faint", label: "Live data off" },
  }[relay.display];

  const key = data?.current_key;
  const credits = data?.credits?.total ?? null;
  const used = data?.usage?.requests_last_24h ?? null;
  const dayLimit = key?.rate_limit_per_day ?? null;
  const frac = used != null && dayLimit ? Math.max(0, Math.min(1, used / dayLimit)) : 0;

  return (
    <Section label="Account">
      {isLoading ? (
        <div className="text-[13px] text-faint">Checking key…</div>
      ) : isError || !data ? (
        <button
          onClick={omDisconnect}
          className="flex min-h-11 w-full items-center justify-center rounded-lg border border-down/40 px-3 text-[13px] font-medium text-down active:bg-down/10"
        >
          Key rejected — connect a different one
        </button>
      ) : (
        <>
          <div className="rounded-lg bg-raised/50 p-3">
            <div className="break-words text-sm font-semibold text-ink">{key?.name ?? key?.key_prefix ?? "API key"}</div>
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px]">
              <span className="rounded border border-border-2 px-1.5 py-0.5 uppercase text-dim">
                {key?.key_type ?? "?"}
              </span>
              <span className={data.can_execute ? "text-up" : "text-faint"}>
                {data.can_execute ? "exec ✓" : "read-only"}
              </span>
              {credits != null && <span className="nums text-dim">{credits.toLocaleString()} credits</span>}
            </div>
          </div>

          {used != null && dayLimit != null && dayLimit > 0 && (
            <div>
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-faint">API usage (24h)</span>
                <span className={`nums ${frac < 0.7 ? "text-dim" : frac < 0.9 ? "text-amber" : "text-down"}`}>
                  {used.toLocaleString()} / {dayLimit.toLocaleString()}
                </span>
              </div>
              <div className="mt-1.5 h-2 overflow-hidden rounded-sm bg-raised">
                <div
                  className={`h-full ${frac < 0.7 ? "bg-blue" : frac < 0.9 ? "bg-amber" : "bg-down"}`}
                  style={{ width: `${Math.round(frac * 100)}%` }}
                />
              </div>
            </div>
          )}

          <div
            className={`flex items-center gap-2 text-[13px] ${ws.text}`}
            title={`relay socket: ${relay.socket} · upstream: ${relay.upstream}`}
          >
            <span className={`h-2 w-2 rounded-full ${ws.dot} ${relay.display === "reconnecting" ? "pulse" : ""}`} />
            {ws.label}
          </div>
        </>
      )}

      <div className="space-y-0.5 pt-1">
        <ActionRow
          onClick={() => {
            onClose();
            showCapabilities();
          }}
        >
          What this key can do
        </ActionRow>
        <ActionRow
          onClick={() => {
            onClose();
            showTour();
          }}
        >
          Replay the tour
        </ActionRow>
        <ActionRow danger onClick={() => omDisconnect()}>
          Sign out
        </ActionRow>
      </div>
    </Section>
  );
}
